import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { feedbackAPI } from '../../services/api';

const DEFAULT_LABELS_5 = [
  { value: 1, label: 'Terrible', emoji: '😡' },
  { value: 2, label: 'Bad', emoji: '😞' },
  { value: 3, label: 'Okay', emoji: '😐' },
  { value: 4, label: 'Good', emoji: '😊' },
  { value: 5, label: 'Excellent', emoji: '🤩' },
];
const DEFAULT_LABELS_3 = [
  { value: 1, label: 'Bad', emoji: '😞' },
  { value: 3, label: 'Okay', emoji: '😐' },
  { value: 5, label: 'Great', emoji: '😍' },
];

const POSITION_OPTIONS = [
  { value: 'bottom-right', label: 'Bottom Right', icon: '↘' },
  { value: 'bottom-left', label: 'Bottom Left', icon: '↙' },
  { value: 'middle-right', label: 'Middle Right', icon: '→' },
  { value: 'middle-left', label: 'Middle Left', icon: '←' },
];

// ─── Live Preview Component ────────────────────────────────────────────────────

const FeedbackPreview: React.FC<{ config: any; onClose: () => void }> = ({ config, onClose }) => {
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const labels = config.rating_labels || [
    { value: 1, label: 'Terrible', emoji: '😡' },
    { value: 2, label: 'Bad', emoji: '😞' },
    { value: 3, label: 'Okay', emoji: '😐' },
    { value: 4, label: 'Good', emoji: '😊' },
    { value: 5, label: 'Excellent', emoji: '🤩' },
  ];
  const showLabels = !!config.show_rating_labels;
  const accent = config.button_color || '#6366f1';
  const accentText = config.button_text_color || '#ffffff';
  const emojiSize = labels.length <= 3 ? 36 : 28;

  const handleFakeSubmit = () => {
    if (!selectedRating) return;
    setShowSuccess(true);
    setTimeout(() => { setShowSuccess(false); setSelectedRating(null); }, 3000);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(15,15,20,.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '28px 24px 36px', width: '100%', maxWidth: 460, boxShadow: '0 -8px 40px rgba(0,0,0,.18)', maxHeight: '90vh', overflowY: 'auto', animation: 'ca-preview-in .26s cubic-bezier(.22,1,.36,1) both' }}
        onClick={e => e.stopPropagation()}
      >
        <style>{`@keyframes ca-preview-in{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>
        {/* Handle */}
        <div style={{ width: 36, height: 4, background: '#e2e8f0', borderRadius: 9, margin: '0 auto 20px' }} />
        {/* Close */}
        <button onClick={onClose} style={{ position: 'absolute' as any, top: 20, right: 20, background: '#f1f5f9', border: 'none', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 15, color: '#64748b' }}>✕</button>

        {!showSuccess ? (
          <>
            {/* Badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 20, padding: '4px 10px', fontSize: 11, color: '#16a34a', fontWeight: 600, marginBottom: 14 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} /> Preview — not real
            </div>
            <h3 style={{ margin: '0 0 4px', fontSize: 19, fontWeight: 700, color: '#0f172a', fontFamily: 'inherit' }}>Share Your Feedback</h3>
            <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: 14 }}>How was your experience? Your opinion matters.</p>

            {/* Ratings */}
            <div style={{ display: 'flex', gap: labels.length <= 3 ? 16 : 8, justifyContent: 'center', marginBottom: 18 }}>
              {labels.map((lbl: any) => (
                <div
                  key={lbl.value}
                  onClick={() => setSelectedRating(lbl.value)}
                  style={{
                    cursor: 'pointer', textAlign: 'center', padding: labels.length <= 3 ? '12px 18px' : '8px 10px',
                    borderRadius: 14, border: `2px solid ${selectedRating === lbl.value ? accent : '#e2e8f0'}`,
                    background: selectedRating === lbl.value ? accent + '22' : '',
                    transition: 'all .18s',
                    transform: selectedRating === lbl.value ? 'scale(1.1) translateY(-2px)' : '',
                  }}
                >
                  <div style={{ fontSize: emojiSize, lineHeight: 1.2 }}>{lbl.emoji}</div>
                  {showLabels && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 5, fontWeight: 500 }}>{lbl.label}</div>}
                </div>
              ))}
            </div>

            {config.show_comment && (
              <textarea placeholder="Tell us more... (optional)" rows={3} style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'none', marginBottom: 12, outline: 'none' }} />
            )}
            {config.show_email && (
              <input type="email" placeholder="Your email (optional)" style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 12, outline: 'none' }} />
            )}
            {config.show_phone && (
              <input type="tel" placeholder="Your phone (optional)" style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 12, outline: 'none' }} />
            )}
            {!selectedRating && (
              <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', marginBottom: 10 }}>Select a rating above to continue</div>
            )}
            <button
              onClick={handleFakeSubmit}
              disabled={!selectedRating}
              style={{ width: '100%', background: selectedRating ? accent : '#e2e8f0', color: selectedRating ? accentText : '#94a3b8', border: 'none', borderRadius: 10, padding: 12, fontSize: 15, fontWeight: 700, cursor: selectedRating ? 'pointer' : 'not-allowed', transition: 'all .15s' }}
            >
              Submit Feedback
            </button>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
            <h4 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{config.thank_you_title || 'Thank you!'}</h4>
            <p style={{ margin: 0, color: '#64748b', fontSize: 15, lineHeight: 1.5 }}>{config.thank_you_message || 'We really appreciate your feedback.'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

const FeedbackWidgetPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const isIntegrated = !!(currentAsset?.is_verified);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showPreview, setShowPreview] = useState(false);
  const [config, setConfig] = useState<any>({
    asset_id: assetId,
    is_enabled: 0,
    widget_type: 'button',
    button_position: 'bottom-right',
    button_text: 'Feedback',
    button_style: 'text',
    button_icon: '💬',
    button_color: '#6366f1',
    button_text_color: '#ffffff',
    popup_trigger: 'exit_intent',
    popup_delay_seconds: 5,
    popup_frequency_weeks: 1,
    rating_labels: DEFAULT_LABELS_5,
    rating_count: 5,
    show_rating_labels: 1,
    show_comment: 1,
    show_email: 0,
    show_phone: 0,
    extra_questions: [],
    captcha_threshold: 3,
    thank_you_title: 'Thank you!',
    thank_you_message: 'We really appreciate your feedback. It helps us improve!',
    notify_on_submit: 0,
  });

  useEffect(() => {
    if (!assetId) return;
    setLoading(true);
    feedbackAPI.getConfig(assetId)
      .then(res => { const d = res.data.data; setConfig({ ...d, asset_id: assetId }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [assetId]);

  const set = (key: string, value: any) => setConfig((prev: any) => ({ ...prev, [key]: value }));

  const handleRatingCountChange = (count: number) => {
    set('rating_count', count);
    // Reset labels to defaults for the chosen count
    set('rating_labels', count === 3 ? DEFAULT_LABELS_3 : DEFAULT_LABELS_5);
  };

  const handleSave = async () => {
    if (!assetId) return;
    setSaving(true);
    try {
      await feedbackAPI.updateConfig({ ...config, asset_id: assetId });
      toast.success('Widget settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    const q = { id: `q_${Date.now()}`, question: '', type: 'text', options: [] };
    set('extra_questions', [...(config.extra_questions || []), q]);
  };

  const removeQuestion = (idx: number) => {
    set('extra_questions', config.extra_questions.filter((_: any, i: number) => i !== idx));
  };

  const updateQuestion = (idx: number, field: string, value: any) => {
    const updated = config.extra_questions.map((q: any, i: number) => i === idx ? { ...q, [field]: value } : q);
    set('extra_questions', updated);
  };

  const updateLabel = (idx: number, field: string, value: string) => {
    const labels = config.rating_labels || DEFAULT_LABELS_5;
    set('rating_labels', labels.map((l: any, i: number) => i === idx ? { ...l, [field]: value } : l));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      </DashboardLayout>
    );
  }

  const labels = config.rating_labels || DEFAULT_LABELS_5;
  const isIconBtn = config.button_style === 'icon';
  const isPopup = config.widget_type === 'popup' || config.widget_type === 'both';
  const isButton = config.widget_type === 'button' || config.widget_type === 'both';

  return (
    <DashboardLayout>
      <div className="container-fluid py-4" style={{ maxWidth: 880 }}>

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="fw-bold mb-0">
              <i className="bi bi-chat-square-text text-primary me-2"></i>Feedback Widget Settings
            </h4>
            <p className="text-muted small mb-0">Configure how the feedback widget looks and behaves on your website.</p>
          </div>
          <a href="/feedback" className="btn btn-outline-secondary btn-sm">
            <i className="bi bi-list-ul me-1"></i>View Feedbacks
          </a>
        </div>

        {/* Integration notice */}
        {!isIntegrated && (
          <div className="alert alert-warning d-flex align-items-start gap-2 mb-3 py-2">
            <i className="bi bi-exclamation-triangle-fill mt-1 flex-shrink-0"></i>
            <div className="small">
              <strong>Tracking script not detected</strong> — The widget is injected automatically by <code>cloudayn.js</code>.
              It won't appear on your website until the script is added.{' '}
              <a href="/integration" className="alert-link">Add integration script →</a>
            </div>
          </div>
        )}

        {/* Enable */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body d-flex align-items-center justify-content-between">
            <div>
              <div className="fw-semibold">Enable Feedback Widget</div>
              <div className="text-muted small">
                When enabled, the widget will appear automatically on your website via <code>cloudayn.js</code>.
                {isIntegrated
                  ? <span className="text-success ms-1"><i className="bi bi-check-circle-fill"></i> Script detected</span>
                  : <span className="text-warning ms-1"><i className="bi bi-exclamation-circle-fill"></i> Script not detected</span>}
              </div>
            </div>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch"
                checked={!!config.is_enabled}
                onChange={e => set('is_enabled', e.target.checked ? 1 : 0)}
                style={{ width: 46, height: 24 }}
              />
            </div>
          </div>
        </div>

        {/* Widget Type */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-transparent border-0 pt-3 pb-1 fw-semibold">
            <i className="bi bi-layout-text-sidebar me-2 text-primary"></i>Display Mode
          </div>
          <div className="card-body pt-2">
            <div className="d-flex gap-3">
              {[
                { v: 'button', label: 'Floating Button', desc: 'Always-visible button on the page', icon: 'bi-chat-square-fill' },
                { v: 'popup', label: 'Auto Popup', desc: 'Appears automatically on trigger', icon: 'bi-window-stack' },
                { v: 'both', label: 'Both', desc: 'Button + auto popup combined', icon: 'bi-layers-fill' },
              ].map(t => (
                <label key={t.v} className={`border rounded-3 p-3 text-center flex-fill ${config.widget_type === t.v ? 'border-primary bg-primary bg-opacity-10' : ''}`} style={{ cursor: 'pointer' }}>
                  <input type="radio" className="visually-hidden" value={t.v} checked={config.widget_type === t.v} onChange={() => set('widget_type', t.v)} />
                  <i className={`bi ${t.icon} fs-4 d-block mb-1 ${config.widget_type === t.v ? 'text-primary' : 'text-muted'}`}></i>
                  <div className="fw-semibold small">{t.label}</div>
                  <div className="text-muted" style={{ fontSize: 11 }}>{t.desc}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Button Config */}
        {isButton && (
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-transparent border-0 pt-3 pb-1 fw-semibold">
              <i className="bi bi-ui-radios me-2 text-primary"></i>Button Appearance
            </div>
            <div className="card-body pt-2">
              {/* Button style tabs */}
              <div className="mb-3">
                <label className="form-label small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '.5px' }}>Button Style</label>
                <div className="d-flex gap-2">
                  <label className={`border rounded-3 p-3 flex-fill text-center ${config.button_style !== 'icon' ? 'border-primary bg-primary bg-opacity-10' : ''}`} style={{ cursor: 'pointer', maxWidth: 200 }}>
                    <input type="radio" className="visually-hidden" checked={config.button_style !== 'icon'} onChange={() => set('button_style', 'text')} />
                    <div className="mb-2">
                      <button
                        style={{ background: config.button_color, color: config.button_text_color, border: 'none', borderRadius: 20, padding: '6px 14px', fontWeight: 600, fontSize: 13, cursor: 'default' }}
                        disabled
                      >
                        {config.button_icon || '💬'} {config.button_text || 'Feedback'}
                      </button>
                    </div>
                    <div className="small fw-semibold">Pill Button</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Icon + text label</div>
                  </label>
                  <label className={`border rounded-3 p-3 flex-fill text-center ${config.button_style === 'icon' ? 'border-primary bg-primary bg-opacity-10' : ''}`} style={{ cursor: 'pointer', maxWidth: 200 }}>
                    <input type="radio" className="visually-hidden" checked={config.button_style === 'icon'} onChange={() => set('button_style', 'icon')} />
                    <div className="mb-2">
                      <button
                        style={{ background: config.button_color, color: config.button_text_color, border: 'none', borderRadius: '50%', width: 46, height: 46, fontSize: 20, cursor: 'default', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                        disabled
                      >
                        {config.button_icon || '💬'}
                      </button>
                    </div>
                    <div className="small fw-semibold">Icon Only</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Minimal circle button</div>
                  </label>
                </div>
              </div>

              <div className="row g-3">
                {/* Position */}
                <div className="col-md-6">
                  <label className="form-label small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '.5px' }}>Position</label>
                  <div className="d-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {POSITION_OPTIONS.map(p => (
                      <label key={p.value} className={`border rounded-3 px-3 py-2 d-flex align-items-center gap-2 ${config.button_position === p.value ? 'border-primary bg-primary bg-opacity-10' : ''}`} style={{ cursor: 'pointer' }}>
                        <input type="radio" className="visually-hidden" checked={config.button_position === p.value} onChange={() => set('button_position', p.value)} />
                        <span style={{ fontSize: 18, fontWeight: 700 }}>{p.icon}</span>
                        <div>
                          <div className="small fw-semibold">{p.label}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="form-text mt-1">
                    <i className="bi bi-info-circle me-1"></i>
                    Middle positions attach to the edge of the screen like a tab.
                  </div>
                </div>

                {/* Colors + text */}
                <div className="col-md-6">
                  <div className="row g-2">
                    <div className="col-12">
                      <label className="form-label small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '.5px' }}>Button Icon (emoji)</label>
                      <input className="form-control" value={config.button_icon || ''} onChange={e => set('button_icon', e.target.value)} placeholder="💬" maxLength={4} />
                      <div className="form-text">Paste any emoji — shown as the button icon or prefix</div>
                    </div>
                    {!isIconBtn && (
                      <div className="col-12">
                        <label className="form-label small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '.5px' }}>Button Text</label>
                        <input className="form-control" value={config.button_text} onChange={e => set('button_text', e.target.value)} placeholder="Feedback" />
                      </div>
                    )}
                    <div className="col-6">
                      <label className="form-label small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '.5px' }}>Background</label>
                      <div className="d-flex gap-2 align-items-center">
                        <input type="color" className="form-control form-control-color" value={config.button_color} onChange={e => set('button_color', e.target.value)} style={{ width: 44, height: 38 }} />
                        <input className="form-control form-control-sm" value={config.button_color} onChange={e => set('button_color', e.target.value)} style={{ fontFamily: 'monospace' }} />
                      </div>
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '.5px' }}>Text Color</label>
                      <div className="d-flex gap-2 align-items-center">
                        <input type="color" className="form-control form-control-color" value={config.button_text_color} onChange={e => set('button_text_color', e.target.value)} style={{ width: 44, height: 38 }} />
                        <input className="form-control form-control-sm" value={config.button_text_color} onChange={e => set('button_text_color', e.target.value)} style={{ fontFamily: 'monospace' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Popup Config */}
        {isPopup && (
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-transparent border-0 pt-3 pb-1 fw-semibold">
              <i className="bi bi-window me-2 text-primary"></i>Popup Trigger
            </div>
            <div className="card-body pt-2">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Trigger</label>
                  <select className="form-select" value={config.popup_trigger} onChange={e => set('popup_trigger', e.target.value)}>
                    <option value="exit_intent">Exit Intent — mouse leaves page</option>
                    <option value="timer">Timer — after delay</option>
                    <option value="scroll">Scroll — after 50% scroll</option>
                  </select>
                </div>
                {config.popup_trigger === 'timer' && (
                  <div className="col-md-3">
                    <label className="form-label small fw-semibold">Delay (seconds)</label>
                    <input type="number" className="form-control" min={1} max={120} value={config.popup_delay_seconds} onChange={e => set('popup_delay_seconds', parseInt(e.target.value) || 5)} />
                  </div>
                )}
                <div className="col-md-3">
                  <label className="form-label small fw-semibold">Show again after (weeks)</label>
                  <input type="number" className="form-control" min={1} max={52} value={config.popup_frequency_weeks} onChange={e => set('popup_frequency_weeks', parseInt(e.target.value) || 1)} />
                  <div className="form-text">Until the visitor submits feedback</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rating */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-transparent border-0 pt-3 pb-1 d-flex align-items-center justify-content-between">
            <span className="fw-semibold"><i className="bi bi-star me-2 text-primary"></i>Rating Options</span>
          </div>
          <div className="card-body pt-2">
            {/* Count */}
            <div className="mb-3">
              <label className="form-label small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '.5px' }}>Number of Rating Options</label>
              <div className="d-flex gap-2">
                {[3, 5].map(n => (
                  <label key={n} className={`border rounded-3 px-4 py-2 text-center ${config.rating_count === n ? 'border-primary bg-primary bg-opacity-10' : ''}`} style={{ cursor: 'pointer', minWidth: 120 }}>
                    <input type="radio" className="visually-hidden" checked={config.rating_count === n} onChange={() => handleRatingCountChange(n)} />
                    <div style={{ fontSize: 20 }}>
                      {n === 3 ? '😞 😐 😍' : '😡 😞 😐 😊 🤩'}
                    </div>
                    <div className="small fw-semibold mt-1">{n} options</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {n === 3 ? 'Simple & fast' : 'Full scale'}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Show labels toggle */}
            <div className="form-check form-switch mb-3">
              <input className="form-check-input" type="checkbox" role="switch" id="showRatingLabels"
                checked={!!config.show_rating_labels}
                onChange={e => set('show_rating_labels', e.target.checked ? 1 : 0)}
              />
              <label className="form-check-label" htmlFor="showRatingLabels">
                <span className="fw-semibold small">Show text labels under emojis</span>
                <span className="text-muted small ms-2">(e.g., "Excellent", "Bad")</span>
              </label>
            </div>

            {/* Emoji + label editor */}
            <div>
              <label className="form-label small fw-semibold text-muted text-uppercase mb-2" style={{ letterSpacing: '.5px' }}>Edit Emojis & Labels</label>
              <div className="d-flex flex-wrap gap-2">
                {labels.map((l: any, i: number) => (
                  <div key={i} className="border rounded-3 p-3 text-center" style={{ minWidth: 110 }}>
                    <input
                      className="form-control form-control-sm text-center mb-2"
                      value={l.emoji}
                      onChange={e => updateLabel(i, 'emoji', e.target.value)}
                      maxLength={4}
                      style={{ fontSize: 22, border: 'none', background: '#f8fafc', borderRadius: 8 }}
                      title="Emoji — paste any emoji"
                    />
                    <input
                      className="form-control form-control-sm text-center"
                      value={l.label}
                      onChange={e => updateLabel(i, 'label', e.target.value)}
                      placeholder={`Label`}
                      style={{ fontSize: 12 }}
                    />
                  </div>
                ))}
              </div>
              <div className="form-text mt-1">Paste any emoji into the emoji field to customise each rating.</div>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-transparent border-0 pt-3 pb-1 fw-semibold">
            <i className="bi bi-input-cursor-text me-2 text-primary"></i>Form Fields
          </div>
          <div className="card-body pt-2">
            <div className="row g-3">
              {[
                { key: 'show_comment', label: 'Comment Box', desc: 'Let users write a comment' },
                { key: 'show_email', label: 'Email Address', desc: 'Collect email for follow-up' },
                { key: 'show_phone', label: 'Phone Number', desc: 'Optional phone collection' },
              ].map(f => (
                <div key={f.key} className="col-md-4">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" role="switch" id={f.key}
                      checked={!!config[f.key]}
                      onChange={e => set(f.key, e.target.checked ? 1 : 0)}
                    />
                    <label className="form-check-label" htmlFor={f.key}>
                      <div className="fw-semibold small">{f.label}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>{f.desc}</div>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Extra Questions */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-transparent border-0 pt-3 pb-1 d-flex align-items-center justify-content-between">
            <span className="fw-semibold"><i className="bi bi-question-circle me-2 text-primary"></i>Extra Questions <span className="text-muted fw-normal small">(optional)</span></span>
            <button className="btn btn-sm btn-outline-primary" onClick={addQuestion}>
              <i className="bi bi-plus me-1"></i>Add Question
            </button>
          </div>
          <div className="card-body pt-2">
            {(!config.extra_questions || config.extra_questions.length === 0) ? (
              <p className="text-muted small mb-0">Add custom text or dropdown questions to collect more structured feedback.</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {config.extra_questions.map((q: any, i: number) => (
                  <div key={q.id} className="border rounded-3 p-3">
                    <div className="row g-2 align-items-center">
                      <div className="col-md-5">
                        <input className="form-control" placeholder="Question text" value={q.question} onChange={e => updateQuestion(i, 'question', e.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <select className="form-select" value={q.type} onChange={e => updateQuestion(i, 'type', e.target.value)}>
                          <option value="text">Text Input</option>
                          <option value="select">Dropdown</option>
                        </select>
                      </div>
                      {q.type === 'select' && (
                        <div className="col-md-3">
                          <input className="form-control" placeholder="Options (comma-separated)" value={(q.options || []).join(', ')} onChange={e => updateQuestion(i, 'options', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} />
                        </div>
                      )}
                      <div className="col-auto ms-auto">
                        <button className="btn btn-sm btn-outline-danger" onClick={() => removeQuestion(i)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Anti-Spam */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-transparent border-0 pt-3 pb-1 fw-semibold">
            <i className="bi bi-shield-check me-2 text-primary"></i>Anti-Spam
          </div>
          <div className="card-body pt-2">
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Interaction threshold for captcha</label>
              <input type="number" className="form-control" min={0} max={20} value={config.captcha_threshold} onChange={e => set('captcha_threshold', parseInt(e.target.value) || 3)} />
              <div className="form-text">
                Show a math captcha if the visitor's interaction score (mouse moves, keystrokes) is below this.
                Set to <strong>0</strong> to always show captcha, or <strong>20</strong> to never show it.
              </div>
            </div>
          </div>
        </div>

        {/* Thank You Message */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-transparent border-0 pt-3 pb-1 fw-semibold">
            <i className="bi bi-emoji-smile me-2 text-primary"></i>Thank You Message
          </div>
          <div className="card-body pt-2">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Title</label>
                <input className="form-control" value={config.thank_you_title || ''} onChange={e => set('thank_you_title', e.target.value)} placeholder="Thank you!" />
              </div>
              <div className="col-md-8">
                <label className="form-label small fw-semibold">Message</label>
                <input className="form-control" value={config.thank_you_message || ''} onChange={e => set('thank_you_message', e.target.value)} placeholder="We really appreciate your feedback. It helps us improve!" />
              </div>
            </div>
            <div className="form-text mt-1">Shown after a user submits feedback.</div>
          </div>
        </div>

        {/* Notifications */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-transparent border-0 pt-3 pb-1 fw-semibold">
            <i className="bi bi-bell me-2 text-primary"></i>Notifications
          </div>
          <div className="card-body pt-2">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" role="switch" id="notifyOnSubmit"
                checked={!!config.notify_on_submit}
                onChange={e => set('notify_on_submit', e.target.checked ? 1 : 0)}
                style={{ width: 40, height: 22 }}
              />
              <label className="form-check-label" htmlFor="notifyOnSubmit">
                <div className="fw-semibold small">Email notification on new feedback</div>
                <div className="text-muted" style={{ fontSize: 12 }}>All organisation members receive an email when someone submits feedback.</div>
              </label>
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-outline-secondary" onClick={() => setShowPreview(true)}>
            <i className="bi bi-eye me-1"></i>Preview Widget
          </button>
          <button className="btn btn-primary px-4" onClick={handleSave} disabled={saving}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
              : <><i className="bi bi-check-lg me-1"></i>Save Settings</>}
          </button>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <FeedbackPreview config={config} onClose={() => setShowPreview(false)} />
        )}

      </div>
    </DashboardLayout>
  );
};

export default FeedbackWidgetPage;
